/**
 * FX + display prices (BRIEF §34): never assume £1 = $1.
 * Card-currency→USD from a public rate API, cached for the session, with
 * conservative fallbacks so demo mode works offline.
 */
import { parseAbi } from "viem";
import { ROBINHOOD_CHAIN } from "./config";
import { robinhoodPublicClient } from "./chains";
import { demoStockPriceCents } from "./swap/mock";
import type { FiatCurrency } from "./types";

const FALLBACK_USD_E6: Record<FiatCurrency, number> = {
  GBP: 1_270_000,
  EUR: 1_100_000,
  USD: 1_000_000,
};

const fxCache = new Map<FiatCurrency, { rateE6: number; fetchedAt: number }>();
const TTL_MS = 10 * 60 * 1000;

export async function getUsdPerCurrencyE6(currency: FiatCurrency): Promise<number> {
  if (currency === "USD") return 1_000_000;
  const cached = fxCache.get(currency);
  if (cached && Date.now() - cached.fetchedAt < TTL_MS) return cached.rateE6;
  try {
    const res = await fetch(`https://api.coinbase.com/v2/exchange-rates?currency=${currency}`, {
      signal: AbortSignal.timeout(5_000),
    });
    const json = await res.json();
    const usd = Number(json?.data?.rates?.USD);
    if (Number.isFinite(usd) && usd > 0.5 && usd < 5) {
      const rateE6 = Math.round(usd * 1_000_000);
      fxCache.set(currency, { rateE6, fetchedAt: Date.now() });
      return rateE6;
    }
  } catch {
    // offline / blocked — fall through
  }
  return fxCache.get(currency)?.rateE6 ?? FALLBACK_USD_E6[currency];
}

export const getUsdPerGbpE6 = () => getUsdPerCurrencyE6("GBP");

/**
 * Stock-token price in USD cents per RAW token unit.
 *
 * Primary source: the official per-stock Chainlink feeds ON Robinhood Chain
 * (free on-chain reads, 8dp, 24/5, live-verified 2026-08-11). The feed
 * reports the TOTAL RETURN value per raw token — the ERC-8056 uiMultiplier
 * is already applied — so `raw units × price` values holdings directly.
 * Fallback: GeckoTerminal pool price, then the static demo table.
 */
const AGGREGATOR_ABI = parseAbi([
  "function latestRoundData() view returns (uint80 roundId, int256 answer, uint256 startedAt, uint256 updatedAt, uint80 answeredInRound)",
]);
const UI_MULTIPLIER_ABI = parseAbi(["function uiMultiplier() view returns (uint256)"]);

/** Feeds update 24/5 — tolerate a weekend + a holiday before distrusting. */
const FEED_MAX_AGE_MS = 4 * 24 * 60 * 60 * 1000;

const stockCache = new Map<string, { cents: number; fetchedAt: number }>();
const STOCK_TTL_MS = 60_000;

async function chainlinkPriceCents(feed: `0x${string}`): Promise<number | null> {
  try {
    const [, answer, , updatedAt] = await robinhoodPublicClient().readContract({
      address: feed,
      abi: AGGREGATOR_ABI,
      functionName: "latestRoundData",
    });
    if (answer <= 0n) return null;
    if (Date.now() - Number(updatedAt) * 1000 > FEED_MAX_AGE_MS) return null;
    return Number(answer / 10n ** 6n); // 8dp → cents
  } catch {
    return null;
  }
}

async function geckoPriceCents(tokenAddress: string): Promise<number | null> {
  try {
    const res = await fetch(
      `https://api.geckoterminal.com/api/v2/networks/robinhood/tokens/${tokenAddress}`,
      { signal: AbortSignal.timeout(6_000), headers: { accept: "application/json" } },
    );
    const json = await res.json();
    const usd = Number(json?.data?.attributes?.price_usd);
    return Number.isFinite(usd) && usd > 0 ? Math.round(usd * 100) : null;
  } catch {
    return null;
  }
}

export async function getStockPriceCents(symbol: string, live: boolean): Promise<number> {
  if (!live) return demoStockPriceCents(symbol);
  const hit = stockCache.get(symbol);
  if (hit && Date.now() - hit.fetchedAt < STOCK_TTL_MS) return hit.cents;
  const token = ROBINHOOD_CHAIN.stockTokens[symbol];
  if (!token) return demoStockPriceCents(symbol);

  const cents =
    (token.chainlinkFeed ? await chainlinkPriceCents(token.chainlinkFeed) : null) ??
    (await geckoPriceCents(token.address));
  if (cents != null) {
    stockCache.set(symbol, { cents, fetchedAt: Date.now() });
    return cents;
  }
  return stockCache.get(symbol)?.cents ?? demoStockPriceCents(symbol);
}

/**
 * ERC-8056 shares-per-token ratio, 1e18-scaled. Raw balance × this / 1e18 =
 * the share count users expect to see. 1e18 (i.e. 1.0) until a split or
 * dividend reinvestment changes it; cached for the session, safe fallback 1.
 */
const multiplierCache = new Map<string, bigint>();

export async function getUiMultiplierE18(symbol: string, live: boolean): Promise<bigint> {
  const ONE = 10n ** 18n;
  if (!live) return ONE;
  const hit = multiplierCache.get(symbol);
  if (hit != null) return hit;
  const token = ROBINHOOD_CHAIN.stockTokens[symbol];
  if (!token) return ONE;
  try {
    const m = await robinhoodPublicClient().readContract({
      address: token.address,
      abi: UI_MULTIPLIER_ABI,
      functionName: "uiMultiplier",
    });
    if (m > 0n) {
      multiplierCache.set(symbol, m);
      return m;
    }
  } catch {
    // token without the extension — treat as 1.0
  }
  return ONE;
}
